import { Box, Skeleton } from "@mui/material";
import {
  CategorySpendDataDto,
  GetPurchasedAssetsSpendWidgetRequest,
  PurchasedAssetsSpendWidgetClient,
} from "@lib/ShiOneClient";
import { graphColors } from "shared-ui";
import { useApi } from "@utils/useApi";
import { useQuery } from "@tanstack/react-query";
import PercentageLine from "@components/PercentageLine";
import { DateTime } from "luxon";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { buildPurchasedAssetsSpendDeepLink } from "./spendWidgetDeepLink";

const useGetSpendByCategory = () => {
  const request = {
    clientLocalNow: DateTime.local().toISO(),
  } as unknown as GetPurchasedAssetsSpendWidgetRequest;
  const api = useApi(PurchasedAssetsSpendWidgetClient);
  return useQuery({
    queryKey: ["purchased-assets-spend-widget", "by-category"],
    queryFn: () => api.getWidgetSpendByCategory(request),
  });
};

function getUniqueMonthsSorted(data: CategorySpendDataDto[]): string[] {
  const uniqueMonths = new Set<string>();

  data.forEach((item) => {
    if (item.year != null && item.month != null) {
      const monthKey = `${item.year}-${String(item.month).padStart(2, "0")}`;
      uniqueMonths.add(monthKey);
    }
  });

  return Array.from(uniqueMonths).sort();
}

function filterByTermMonths(
  data: CategorySpendDataDto[],
  termMonths: number,
): CategorySpendDataDto[] {
  const allMonths = getUniqueMonthsSorted(data);

  const monthsInTerm = new Set(allMonths.slice(-termMonths));

  return data.filter((item) => {
    if (item.year == null || item.month == null) return false;
    const monthKey = `${item.year}-${String(item.month).padStart(2, "0")}`;
    return monthsInTerm.has(monthKey);
  });
}

function aggregateByCategory(
  termSpend: CategorySpendDataDto[],
): Record<string, number> {
  return termSpend.reduce(
    (acc, item) => {
      if (!item.category) return acc;
      acc[item.category] = (acc[item.category] || 0) + (item.amount ?? 0);
      return acc;
    },
    {} as Record<string, number>,
  );
}

function sortCategorySpend(
  accumulated: Record<string, number>,
): [string, number][] {
  return Object.entries(accumulated)
    .map(
      ([key, value]) =>
        [key, Math.round(value * 100) / 100] as [string, number],
    )
    .sort((a, b) => b[1] - a[1]);
}

function prepareChartData(
  sortedCategorySpend: [string, number][],
  colorPalette: string[],
) {
  return sortedCategorySpend.map(([category, total], idx) => ({
    name: "CategoryByManufacturer",
    label: category,
    data: total,
    color: colorPalette[idx % colorPalette.length],
  }));
}

const AssetsSpendByCategory = ({ termMonths }: { termMonths: number }) => {
  const navigate = useNavigate();
  const colorPalette = graphColors;
  const { data: response, isLoading } = useGetSpendByCategory();

  const chartData = useMemo(() => {
    const rawData = response?.data?.categorySpendData ?? [];

    const termSpend = filterByTermMonths(rawData, termMonths);
    const categorySpend = aggregateByCategory(termSpend);
    const sortedCatSpend = sortCategorySpend(categorySpend);

    return prepareChartData(sortedCatSpend, colorPalette);
  }, [response?.data?.categorySpendData, termMonths]);

  const hasData =
    chartData.length > 0 && chartData.some((item) => item.data > 0);

  const handleCategoryLabelClick = (category: string) => {
    const deepLink = buildPurchasedAssetsSpendDeepLink({
      viewBy: "category",
      termMonths,
      category,
    });
    navigate(deepLink);
  };

  return (
    <>
      {isLoading && (
        <Box sx={{ padding: "16px" }}>
          <Skeleton
            variant="rectangular"
            height={150}
            sx={{ mb: 1.5, borderRadius: 1 }}
          />
        </Box>
      )}
      {!isLoading && !hasData && (
        <Box display="flex" sx={{ p: 2, justifyContent: "space-between" }}>
          <Box sx={{ fontWeight: 500 }}>Spend by Category</Box>
        </Box>
      )}
      {!isLoading && hasData && (
        <PercentageLine
          visualParts={chartData}
          loading={false}
          leftLabel={"Spend by Category"}
          containerStyle={{ padding: "20px" }}
          showSummaryValue={false}
          onLabelClick={handleCategoryLabelClick}
        />
      )}
    </>
  );
};

export default AssetsSpendByCategory;
